
import Header from '@/components/header/ModernHeader';
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Phone, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import BackButton from "@/components/navigation/BackButton";
import { useContactForm } from "@/hooks/useContactForm";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const Contact = () => {
  const { form, onSubmit, isSubmitting, isSuccess, charCounts } = useContactForm();
  
  // Fetch contact email from site config
  const { data: siteConfig } = useQuery({
    queryKey: ['public-site-config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('public_site_config')
        .select('contact_email')
        .eq('id', 'main')
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  return (
    <>
      <SEO title="Contact Us" description="Get in touch with our team. We're here to help!" />
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="w-full px-4 md:px-8 lg:px-12 py-16">
          <div className="max-w-4xl mx-auto">
            <BackButton to="/" label="Back to Home" />
            <h1 className="text-4xl font-bold text-center mb-8">Contact Us</h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Get in Touch</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">Email</p>
                      <a 
                        href={`mailto:${siteConfig?.contact_email || 'support@example.com'}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {siteConfig?.contact_email || 'support@example.com'}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">Phone</p>
                      <p className="text-muted-foreground">+1 (555) 123-4567</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">Address</p>
                      <p className="text-muted-foreground">123 Main St, City, State 12345</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Form */}
              <Card>
                <CardHeader>
                  <CardTitle>Send us a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  {isSuccess && (
                    <Alert className="mb-6 border-green-200 bg-green-50">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-800">
                        Your message has been sent successfully! We'll get back to you soon.
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <form onSubmit={onSubmit} className="space-y-4">
                    {/* Honeypot field - hidden from users */}
                    <div className="hidden" aria-hidden="true">
                      <Label htmlFor="honeypot">Leave this field empty</Label>
                      <Input 
                        id="honeypot" 
                        type="text" 
                        tabIndex={-1}
                        autoComplete="off"
                        {...form.register('honeypot')}
                      />
                    </div>

                    <div>
                      <Label htmlFor="name">
                        Name <span className="text-destructive">*</span>
                      </Label>
                      <Input 
                        id="name" 
                        type="text" 
                        disabled={isSubmitting}
                        {...form.register('name')}
                      />
                      <div className="flex justify-between mt-1">
                        {form.formState.errors.name && (
                          <p className="text-sm text-destructive">
                            {form.formState.errors.name.message}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground ml-auto">
                          {charCounts.name}/100
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">
                        Email <span className="text-destructive">*</span>
                      </Label>
                      <Input 
                        id="email" 
                        type="email" 
                        disabled={isSubmitting}
                        {...form.register('email')}
                      />
                      {form.formState.errors.email && (
                        <p className="text-sm text-destructive mt-1">
                          {form.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="subject">
                        Subject <span className="text-destructive">*</span>
                      </Label>
                      <Input 
                        id="subject" 
                        type="text" 
                        disabled={isSubmitting}
                        {...form.register('subject')}
                      />
                      <div className="flex justify-between mt-1">
                        {form.formState.errors.subject && (
                          <p className="text-sm text-destructive">
                            {form.formState.errors.subject.message}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground ml-auto">
                          {charCounts.subject}/200
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">
                        Message <span className="text-destructive">*</span>
                      </Label>
                      <Textarea 
                        id="message" 
                        rows={5} 
                        disabled={isSubmitting}
                        {...form.register('message')}
                      />
                      <div className="flex justify-between mt-1">
                        {form.formState.errors.message && (
                          <p className="text-sm text-destructive">
                            {form.formState.errors.message.message}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground ml-auto">
                          {charCounts.message}/2000
                        </p>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default Contact;
